<script lang="ts">
export default {
  metaInfo: () => ({
    title: "Billing and Payment | TinyURL: The Original Link Shortener",
  }),
};
</script>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import type { RawLocation } from "vue-router";
import { isNullish } from "@tinyurldev/tu-ui-vue";

import BillingApi from "@Modules/billing/Api/BillingApi";
import BillingPageView from "@Modules/billing/Views/BillingPageView.vue";
import BillingViewModel, { type TransactionFeedbackBanner } from "@Modules/billing/ViewModels/BillingViewModel";
import SubscriptionModel, { type Subscription, type SubscriptionData } from "@Modules/subscriptions/Data/Models/SubscriptionModel";
import SettingsTopBar from "@Modules/teams/components/SettingsTopBar.vue";
import { useCurrentOrganization } from "@Modules/teams/composables/useCurrentOrganization";
import OrgSettingsLayout from "@Modules/teams/Views/OrgSettingsLayout.vue";
import { XAuthStore } from "@Modules/users/Store/XAuthStore";
// The unpaid overage, the past due subscription and the user's currency only live in the
// legacy vuex state; no Pinia store carries them yet, and the controller is where the page
// resolves its data (docs/teams/mvc-page-structure.md).
// eslint-disable-next-line no-restricted-imports
import useLegacyStore from "@Composables/useLegacyStore";
import useNotify from "@Composables/useNotify";
import usePaddle from "@Composables/usePaddle";
import usePaymentMethod from "@Composables/usePaymentMethod";
import useStripeLatestPayment from "@Composables/useStripeLatestPayment";
import useSubscription from "@Composables/useSubscription";
import useTranslation from "@Composables/useTranslation";
import BILLABLE_TYPE from "@Constants/billable-type";
import BILLING_TYPE from "@Constants/billing-type";
import SUBSCRIPTION_TYPE from "@Constants/subscription-type";
import { logError } from "@Helpers/logger";
import { currencyFormat } from "@Helpers/support";
import SettingsContentLayout from "@Layouts/settings/LayoutContent.vue";

//
// Setup
const { currentOrganizationSlug, currentOrganization, currentWorkspace } = useCurrentOrganization();
const { openPaddleCompactCheckoutByTransaction } = usePaddle();
const { __ } = useTranslation();
const { notifyError } = useNotify();
const legacyStore = useLegacyStore();
const paymentMethod = usePaymentMethod();
const { handleStripeSubscription } = useSubscription();
const auth = XAuthStore();
const billingApi = BillingApi();

/**
 * The payment a past due stripe subscription already has open, looked up on whichever
 * billable the page is showing.
 */
const { getLatestPaymentLink } = useStripeLatestPayment(
  computed(() => (isNullish(currentOrganizationSlug.value) ? BILLABLE_TYPE.USER : BILLABLE_TYPE.ORGANIZATION)),
  currentOrganization,
);

//
// Data
/** True once the organization resource that carries the org billing data has been loaded */
const isOrganizationLoaded = ref(false);

//
// Computed
const isOrgContext = computed<boolean>(() => !isNullish(currentOrganizationSlug.value));

const canManageSubscriptions = computed<boolean>(() => {
  return currentOrganization.value?.can("manage_subscriptions") ?? false;
});

/**
 * The billable's subscriptions. The organization keeps them on the full organization
 * resource, the single user carries them in the application state.
 */
const subscriptions = computed<SubscriptionData[]>(() => {
  if (isOrgContext.value) {
    return billingApi.orgSubscriptions.value;
  }

  return auth.user?.subscriptions?.current ?? [];
});

/**
 * The single user's subscriptions are in the store from the start, the organization's
 * are only known once the organization has been fetched.
 */
const subscriptionsReady = computed<boolean>(() => {
  return isOrgContext.value ? isOrganizationLoaded.value : true;
});

const vm = BillingViewModel({
  orgSlug: currentOrganizationSlug,
  subscriptions,
  billingApi,
  openCompactCheckout: openPaddleCompactCheckoutByTransaction,
  afterPaymentSettled: refreshOrganizationBilling,
  afterBillableChanged: refreshBillable,
});

/**
 * The billing type of the billable, which decides whether the coupon section is offered.
 */
const billingType = computed<number | null>(() => {
  if (!isOrgContext.value) {
    return auth.billingType;
  }

  const defaultSubscription = subscriptions.value.find(
    (subscription) => subscription.type === SUBSCRIPTION_TYPE.DEFAULT,
  );

  return (defaultSubscription ?? subscriptions.value[0])?.billing_type ?? null;
});

/**
 * Whether the billable is an organization billed through Stripe, which is the only case
 * the extra billing information and coupon sections are offered in.
 *
 * Both ride on the organization the team is billed on, so the single user is offered
 * neither. Coupons are a Stripe feature on top of that: Paddle answers the redemption
 * with a refusal and the lookup with nothing at all.
 */
const isStripeSubscription = computed<boolean>(() => {
  return isOrgContext.value && billingType.value === BILLING_TYPE.STRIPE;
});

/** Whether the application charges European VAT, which is a global setting */
const isVatIdVisible = computed<boolean>(() => auth.collectsEuropeanVat);

/** The VAT id the server holds for the billable */
const vatId = computed<string>(() => {
  if (isOrgContext.value) {
    return billingApi.orgVatId.value ?? "";
  }

  return auth.user?.vat_id ?? "";
});

/**
 * Support can hide a user's invoices from them, and then the transaction block goes with
 * them. Only the single user carries the flag; no organization equivalent exists.
 */
const areInvoicesHidden = computed<boolean>(() => !isOrgContext.value && auth.userModel.hasHiddenInvoices());

/** The overage the billable still owes, in minor units */
const unpaidOverage = computed<number>(() => {
  if (isOrgContext.value) {
    return billingApi.orgUnpaidOverage.value;
  }

  return legacyStore?.getters?.unpaidOverage ?? 0;
});

const hasUnpaidOverage = computed<boolean>(() => {
  if (isOrgContext.value) {
    return unpaidOverage.value > 0;
  }

  return legacyStore?.getters?.hasUnpaidOverage === true;
});

const currentSubscription = computed(() => legacyStore?.getters?.getUserCurrentSubscription ?? null);

/** The subscription the organization is billed on, or null while it has none */
const organizationSubscription = computed<Subscription | null>(() => {
  if (!isOrgContext.value) {
    return null;
  }

  const defaultSubscription = subscriptions.value.find(
    (subscription) => subscription.type === SUBSCRIPTION_TYPE.DEFAULT,
  );

  if (isNullish(defaultSubscription)) {
    return null;
  }

  return SubscriptionModel(defaultSubscription, currentOrganizationSlug.value);
});

const userBillingType = computed<number | null>(() => legacyStore?.getters?.getUserBillingType ?? null);

/** The gateway the outstanding balance is settled through */
const payAllBillingType = computed<number | null>(() => {
  return isOrgContext.value ? billingType.value : userBillingType.value;
});

/** Whether the charge the billable is subscribed for failed to collect */
const isSubscriptionPastDue = computed<boolean>(() => {
  if (isOrgContext.value) {
    return organizationSubscription.value?.isPastDue() ?? false;
  }

  return currentSubscription.value?.isPastDue === true;
});

/** What settling that failed charge costs, in minor units */
const pastDueAmount = computed<number>(() => {
  if (isOrgContext.value) {
    return organizationSubscription.value?.amount ?? 0;
  }

  return currentSubscription.value?.amount ?? 0;
});

/**
 * Everything the billable still owes: the unpaid overage, plus the subscription
 * charge when that one is past due as well.
 */
const outstandingBalance = computed<number>(() => {
  if (isSubscriptionPastDue.value) {
    return unpaidOverage.value + pastDueAmount.value;
  }

  return unpaidOverage.value;
});

const formattedBalance = computed<string>(() => {
  if (isOrgContext.value) {
    return currencyFormat(
      outstandingBalance.value,
      billingApi.orgCurrency.value ?? legacyStore?.getters?.userCurrency,
      auth.locale,
    );
  }

  return currencyFormat(
    outstandingBalance.value,
    legacyStore?.getters?.userCurrency,
    legacyStore?.getters?.userLocale,
  );
});

/**
 * The banner the transaction table wears while the billable owes something, or null
 * while there is nothing left unsettled.
 *
 * The wording carries a link, so it goes through the "feedback-banner-content" slot
 * rather than the banner's own content property.
 */
const transactionFeedbackBanner = computed<TransactionFeedbackBanner | null>(() => {
  if (!hasUnpaidOverage.value) {
    return null;
  }

  return {
    isClosable: false,
    feedbackButton: {
      label: __("settings_billing_page.transaction.feedback_banner.pay_all"),
    },
  };
});

const viewAllRoute = computed<RawLocation>(() => {
  if (isOrgContext.value) {
    return {
      name: "org.settings.profile.payment.transactions",
      params: { org: currentOrganizationSlug.value ?? "" },
    };
  }

  return { name: "settings.billing.transactions" };
});

//
// Methods
/**
 * Reloads the organization's billing data once a payment has settled, so the banner goes
 * away with the overage it was raised for. The single user gets the same from the state
 * refresh usePaddle runs after a checkout.
 */
async function refreshOrganizationBilling(): Promise<void> {
  const orgSlug = currentOrganizationSlug.value;

  if (orgSlug === null) {
    return;
  }

  await billingApi.fetchOrganizationBilling(orgSlug);
}

/**
 * Reads the billable back from the server after a change to what it carries, so the
 * page keeps showing what is stored rather than what it was rendered with. The single
 * user's own record lives in the application state, the organization's on its resource.
 */
async function refreshBillable(): Promise<void> {
  const orgSlug = currentOrganizationSlug.value;

  if (orgSlug === null) {
    await auth.refreshUserState();

    return;
  }

  await billingApi.fetchOrganizationBilling(orgSlug);
}

/**
 * Settles the past due subscription along with the overage it comes with: stripe sends
 * the browser to the payment it already has open, paddle reopens the payment method.
 *
 * The organization reopens it through the module's own flow, which scopes the checkout
 * session request to the organization; the legacy composable knows the single user url
 * only.
 */
async function paySubscriptionAndOverages(): Promise<void> {
  try {
    if (payAllBillingType.value === BILLING_TYPE.STRIPE) {
      const latestPaymentLink = await getLatestPaymentLink();

      if (isNullish(latestPaymentLink)) {
        throw new Error("The latest payment is missing.");
      }

      window.location.href = latestPaymentLink;

      return;
    }

    if (isOrgContext.value) {
      const defaultRow = vm.subscriptionRows.value.find((row) => row.type === SUBSCRIPTION_TYPE.DEFAULT);

      if (defaultRow === undefined) {
        throw new Error("The organization has no subscription to update.");
      }

      await vm.updatePaymentMethod(defaultRow.id);

      return;
    }

    await paymentMethod.updatePaymentMethod(currentSubscription.value);
  } catch (error) {
    logError(`The payment method update failed: ${String(error)}`);
    notifyError({ text: __("failed_getting_latest_payment") });
  }
}

/**
 * Settles the whole outstanding balance from the banner.
 */
function payAll(): void {
  if (isSubscriptionPastDue.value) {
    paySubscriptionAndOverages();

    return;
  }

  const billingType = payAllBillingType.value;

  if (isNullish(billingType)) {
    logError("The billing type of the billable is missing.");

    return;
  }

  vm.payAllUnpaid(billingType, handleStripeSubscription);
}

//
// Watchers
// An overage settled somewhere else changes what the invoices say, so the table is read
// again rather than left showing the rows it was painted with. The organization's own
// first load is not such a change: it brings the overage in from nothing, and the
// invoices are read for it right afterwards anyway.
watch(unpaidOverage, () => {
  if (isOrgContext.value && !isOrganizationLoaded.value) {
    return;
  }

  if (areInvoicesHidden.value) {
    return;
  }

  vm.loadInvoices();
});

//
// Lifecycle
onMounted(async () => {
  vm.reset();

  const orgSlug = currentOrganizationSlug.value;

  if (orgSlug === null) {
    if (!areInvoicesHidden.value) {
      await vm.loadInvoices();
    }

    return;
  }

  if (!canManageSubscriptions.value) {
    return;
  }

  await billingApi.fetchOrganizationBilling(orgSlug);

  isOrganizationLoaded.value = true;

  await vm.loadInvoices();

  // The organization's billing type only becomes known once its resource has landed,
  // so the discount is asked for after that rather than on the way in.
  if (isStripeSubscription.value) {
    await vm.loadDiscount();
  }
});

//
// Exposed Test Vars
defineExpose({
  isOrgContext,
  canManageSubscriptions,
  subscriptions,
  subscriptionsReady,
  billingType,
  isStripeSubscription,
  isVatIdVisible,
  vatId,
  areInvoicesHidden,
  unpaidOverage,
  hasUnpaidOverage,
  isSubscriptionPastDue,
  payAllBillingType,
  outstandingBalance,
  formattedBalance,
  transactionFeedbackBanner,
  payAll,
  viewAllRoute,
  refreshOrganizationBilling,
  vm,
});
</script>

<template>
  <OrgSettingsLayout
    v-if="isOrgContext"
    class="_billing-page"
    :organization="currentOrganization"
    :workspace="currentWorkspace"
    :can="canManageSubscriptions"
  >
    <SettingsTopBar :title="__('settings_billing_page.page_title')" />

    <BillingPageView
      class="_billing_page"
      :subscription-rows="vm.subscriptionRows.value"
      :subscriptions-ready="subscriptionsReady"
      :requested-subscription-id="vm.requestedSubscriptionId.value"
      :updated-subscription-id="vm.updatedSubscriptionId.value"
      :manage-subscription="vm.updatePaymentMethod"
      :invoices="vm.latestInvoices.value"
      :invoices-ready="vm.invoicesReady.value"
      :download-invoice="vm.downloadInvoice"
      :pay-invoice="vm.payInvoice"
      :open-hosted-invoice="vm.openHostedInvoice"
      :paying-invoice-id="vm.payingInvoiceId.value"
      :hosted-invoice-id="vm.hostedInvoiceId.value"
      :feedback-banner="transactionFeedbackBanner"
      :pay-all="payAll"
      :invoice-count="vm.downloadableInvoices.value.length"
      :view-all-route="viewAllRoute"
      :discount="vm.discount.value"
      :are-invoices-hidden="areInvoicesHidden"
      :is-coupon-visible="isStripeSubscription"
      :coupon-errors="vm.couponErrors.value"
      :redeem-coupon="vm.redeemCoupon"
      :is-vat-id-visible="isVatIdVisible"
      :vat-id="vatId"
      :vat-id-errors="vm.vatIdErrors.value"
      :submit-vat-id="vm.submitVatId"
    >
      <template #feedback-banner-content>
        <span class="_billing_balance_amount">{{ __("transactions.outstanding_balance", { balance: formattedBalance }) }}</span> <br>
        {{ __("transactions.contact_support") }}
        <RouterLink
          class="billing-settings__contact-link _billing_contact_link"
          :to="{ name: 'freescout' }"
        >
          <span>{{ __("transactions.contact_us") }}</span>
        </RouterLink>.
      </template>
    </BillingPageView>
  </OrgSettingsLayout>

  <SettingsContentLayout
    v-else
    class="_billing-page"
  >
    <template #page-title>
      <span class="_billing_page_title">{{ __("settings_billing_page.page_title") }}</span>
    </template>

    <BillingPageView
      class="_billing_page"
      :subscription-rows="vm.subscriptionRows.value"
      :subscriptions-ready="subscriptionsReady"
      :requested-subscription-id="vm.requestedSubscriptionId.value"
      :updated-subscription-id="vm.updatedSubscriptionId.value"
      :manage-subscription="vm.updatePaymentMethod"
      :invoices="vm.latestInvoices.value"
      :invoices-ready="vm.invoicesReady.value"
      :download-invoice="vm.downloadInvoice"
      :pay-invoice="vm.payInvoice"
      :open-hosted-invoice="vm.openHostedInvoice"
      :paying-invoice-id="vm.payingInvoiceId.value"
      :hosted-invoice-id="vm.hostedInvoiceId.value"
      :feedback-banner="transactionFeedbackBanner"
      :pay-all="payAll"
      :invoice-count="vm.downloadableInvoices.value.length"
      :view-all-route="viewAllRoute"
      :discount="vm.discount.value"
      :are-invoices-hidden="areInvoicesHidden"
      :is-coupon-visible="isStripeSubscription"
      :coupon-errors="vm.couponErrors.value"
      :redeem-coupon="vm.redeemCoupon"
      :is-vat-id-visible="isVatIdVisible"
      :vat-id="vatId"
      :vat-id-errors="vm.vatIdErrors.value"
      :submit-vat-id="vm.submitVatId"
    >
      <template #feedback-banner-content>
        <span class="_billing_balance_amount">{{ __("transactions.outstanding_balance", { balance: formattedBalance }) }}</span> <br>
        {{ __("transactions.contact_support") }}
        <RouterLink
          class="billing-settings__contact-link _billing_contact_link"
          :to="{ name: 'freescout' }"
        >
          <span>{{ __("transactions.contact_us") }}</span>
        </RouterLink>.
      </template>
    </BillingPageView>
  </SettingsContentLayout>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.billing-settings {
  &__contact-link {
    color: color.$text-white;
    text-decoration: underline;
  }
}
</style>
